// SettingsForm
import React, { useState } from 'react';
import Checkbox from 'components/common/Checkbox';
import Dropdown from 'components/common/Dropdown';
import TextInput from 'components/common/TextInput';
import {
  CREATOR_CATEGORY_TAGS,
  CREATOR_SKILL_TAGS,
} from 'constants/registration';
import cn from 'classnames';
import Button from 'components/common/Button';
import styles from './SettingsForm.module.sass';
import TextArea from 'components/common/TextArea';
import { useFormikContext } from 'formik';
import { AGE_RANGES } from 'types/creator';
import { SettingsFormikValues } from 'constants/settings';
import Avatar from 'components/common/Avatar';
import UppyButton from 'components/common/UppyButton';
import { ContentUploadPreview } from 'types/content';
import {
  CREATOR_ENVIRONMENT_TAGS,
  CREATOR_EQUIPMENT_TAGS,
} from 'constants/profile';
import Loader from 'components/common/Loader';
import { getKeysWithUpdatedValues } from 'utils/object';
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from 'constants/links';

export interface SettingsFormProps {}

const SettingsForm: React.FC<SettingsFormProps> = ({}) => {
  const {
    initialValues,
    values,
    setFieldValue,
    setFieldTouched,
    getFieldProps,
    errors,
    submitForm,
    isSubmitting,
    validateField,
  } = useFormikContext<SettingsFormikValues>();
  const { ageRange } = values;
  const [isUploading, setIsUploading] = useState(false);
  const updatedKeys = getKeysWithUpdatedValues(values, initialValues);
  const disableSubmit =
    Object.keys(errors).length > 0 ||
    isSubmitting ||
    Object.keys(updatedKeys).length === 0;

  const handleAgeRange = (age: AGE_RANGES) => {
    setFieldValue('ageRange', age);
    setFieldTouched('ageRange');
    validateField('ageRange');
  };

  const _handleRemoveProfilePic = (_id: string) => {};

  const handleUploadProfilePic = (img: ContentUploadPreview) => {
    setFieldValue('profilePicture', img.url);
    setFieldTouched('profilePicture');
    validateField('profilePicture');
  };

  return (
    <div className={styles.settingsForm}>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="basics" />
        <h5>Basics</h5>
        <div className={styles.profilePicture}>
          {isUploading ? (
            <div className={styles.avatarLoading}>
              <Loader />
            </div>
          ) : (
            <Avatar
              src={
                values.profilePicture !== '' ? values.profilePicture : undefined
              }
              type="square"
              className={styles.avatar}
            />
          )}
          <UppyButton
            inputId="upload-profile-picture"
            type="primary"
            fullWidth={false}
            icon="add"
            uppyProps={{
              uppyId: 'settings-profile-picture',
              uploadType: 'profilePicture',
              setIsUploading: setIsUploading,
              onUploadSuccess: handleUploadProfilePic,
              onRemove: _handleRemoveProfilePic,
              restrictions: {
                maxNumberOfFiles: 1,
                allowedFileTypes: ['image/*'],
              },
            }}
            uppyClassName={styles.uppyButton}
          >
            Upload New Picture
          </UppyButton>
        </div>
        <TextInput
          label="First name"
          theme="light"
          {...getFieldProps('firstName')}
        />
        <TextInput
          label="Last name"
          theme="light"
          {...getFieldProps('lastName')}
        />
        <TextInput
          label="Username"
          theme="light"
          {...getFieldProps('username')}
        />
        <Dropdown
          label="Age Range"
          value={ageRange}
          setValue={handleAgeRange}
          classDropdownHead={styles.dropdownHead}
          options={Object.values(AGE_RANGES).map((range) => ({
            value: range,
            label: range,
          }))}
        />
        <TextArea
          label="About"
          theme="light"
          rows={5}
          classWrapper={styles.textareaWrapper}
          {...getFieldProps('about')}
        />
      </div>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="categories" />
        <h5>Categories</h5>
        <div className={styles.checkboxContainer}>
          {CREATOR_CATEGORY_TAGS.map(({ label, id }) => (
            <Checkbox
              formikFieldName="socialCategories"
              key={id}
              id={id}
              label={label}
              theme="light"
              labelClassName={styles.checkbox}
              isArray
            />
          ))}
        </div>
      </div>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="skills" />
        <h5>Skills</h5>
        <div className={styles.checkboxContainer}>
          {CREATOR_SKILL_TAGS.map(({ label, id }) => (
            <Checkbox
              formikFieldName="contentCategories"
              key={id}
              id={id}
              label={label}
              theme="light"
              labelClassName={styles.checkbox}
              isArray
            />
          ))}
        </div>
      </div>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="equipment" />
        <h5>Equipment</h5>
        <div className={styles.checkboxContainer}>
          {CREATOR_EQUIPMENT_TAGS.map(({ label, id }) => (
            <Checkbox
              formikFieldName="equipment"
              key={id}
              id={id}
              label={label}
              theme="light"
              labelClassName={styles.checkbox}
              isArray
            />
          ))}
        </div>
      </div>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="environment" />
        <h5>Environment</h5>
        <div className={styles.checkboxContainer}>
          {CREATOR_ENVIRONMENT_TAGS.map(({ label, id }) => (
            <Checkbox
              formikFieldName="environment"
              key={id}
              id={id}
              label={label}
              theme="light"
              labelClassName={styles.checkbox}
              isArray
            />
          ))}
        </div>
      </div>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="social-media-accounts" />
        <h5>Social Media Accounts</h5>
        <TextInput
          label="Instagram"
          startAdornment="@"
          placeholder="trend_io"
          validation={typeof errors.socials?.instagram === 'string'}
          error={errors.socials?.instagram}
          theme="light"
          {...getFieldProps('socials.instagram')}
        />
        <TextInput
          label="TikTok"
          startAdornment="@"
          placeholder="trend"
          validation={typeof errors.socials?.tikTok === 'string'}
          error={errors.socials?.tikTok}
          theme="light"
          {...getFieldProps('socials.tikTok')}
        />
        <TextInput
          label="YouTube"
          placeholder="youtube.com/channel/"
          validation={typeof errors.socials?.youTube === 'string'}
          error={errors.socials?.youTube}
          theme="light"
          {...getFieldProps('socials.youTube')}
        />
        <TextInput
          label="Portfolio Site"
          placeholder="www.trend.io"
          validation={typeof errors.socials?.website === 'string'}
          error={errors.socials?.website}
          theme="light"
          {...getFieldProps('socials.website')}
        />
      </div>
      <div className={styles.sectionContainer}>
        <span className={styles.linkRef} id="paypal-email" />
        <h5>Paypal</h5>
        <TextInput
          {...getFieldProps('payPalEmail')}
          label="PayPal Email"
          theme="light"
        />
      </div>
      <div
        className={cn(styles.sectionContainer, styles.terms)}
        id="privacy-policy"
      >
        <span className={styles.linkRef} id="privacy-policy" />
        <h5>Privacy Policy</h5>
        <a href={PRIVACY_POLICY} target="_blank" rel="noreferrer">
          {PRIVACY_POLICY}
        </a>
      </div>
      <div
        className={cn(styles.sectionContainer, styles.terms)}
        id="terms-of-service"
      >
        <span className={styles.linkRef} id="terms-of-service" />
        <h5>Terms of Service</h5>
        <a href={TERMS_OF_SERVICE} target="_blank" rel="noreferrer">
          {TERMS_OF_SERVICE}
        </a>
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.barContent}>
          <Button
            onClick={submitForm}
            type="primary"
            disabled={disableSubmit}
            className={styles.submitButton}
            fullWidth={false}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsForm;
